/**
 * ── Module Registry ──
 * Single source of truth for every module in MemberOS.
 *
 * Drives: navigation, entitlements, gating, pricing, and access resolution.
 * Do NOT scatter module definitions elsewhere — reference this registry.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type TenantType = "platform" | "association" | "org";

export type NavGroup =
  | "core"
  | "member_success"
  | "engagement"
  | "growth"
  | "revenue"
  | "operations"
  | "intelligence"
  | "association"
  | "platform"
  | "settings";

export type ModuleVisibility = "always" | "entitled" | "hidden";

export interface ModuleDefinition {
  /** Unique key — must match workspace_modules.module_key */
  key: string;
  /** Human-readable label */
  label: string;
  /** Short description for UI cards, tooltips, etc. */
  description: string;
  /** Navigation group for sidebar rendering */
  navGroup: NavGroup;
  /** Which tenant types can see/use this module */
  eligibleTenants: TenantType[];
  /** Default visibility behavior before entitlement resolution */
  defaultVisibility: ModuleVisibility;
  /** Whether this is a core module that's always active */
  isCore: boolean;
  /** Route path if this module has a top-level route */
  route?: string;
  /** Icon name (lucide key) for nav rendering — resolved by consumer */
  iconKey?: string;
}

// ─── Registry ────────────────────────────────────────────────────────────────

export const MODULE_REGISTRY: Record<string, ModuleDefinition> = {
  // ── Core (always active) ──────────────────────────────────────────────────
  core_crm: {
    key: "core_crm",
    label: "CRM & Prospects",
    description: "Contact management, lead tracking, and pipeline management",
    navGroup: "core",
    eligibleTenants: ["org", "association", "platform"],
    defaultVisibility: "always",
    isCore: true,
    route: "/crm",
    iconKey: "Headset",
  },
  core_comms: {
    key: "core_comms",
    label: "Communications",
    description: "Email campaigns, templates, and member messaging",
    navGroup: "core",
    eligibleTenants: ["org", "association", "platform"],
    defaultVisibility: "always",
    isCore: true,
    route: "/communications",
    iconKey: "Mail",
  },
  core_portal: {
    key: "core_portal",
    label: "Member Portal",
    description: "Self-service member dashboard and profile management",
    navGroup: "core",
    eligibleTenants: ["org"],
    defaultVisibility: "always",
    isCore: true,
    iconKey: "UserCheck",
  },
  core_events: {
    key: "core_events",
    label: "Events",
    description: "Event creation, registration, ticketing, and check-in",
    navGroup: "engagement",
    eligibleTenants: ["org", "association"],
    defaultVisibility: "always",
    isCore: true,
    route: "/events",
    iconKey: "CalendarDays",
  },
  core_billing: {
    key: "core_billing",
    label: "Billing & Payments",
    description: "Invoicing, subscriptions, and payment processing",
    navGroup: "revenue",
    eligibleTenants: ["org", "association", "platform"],
    defaultVisibility: "always",
    isCore: true,
    route: "/billing",
    iconKey: "CreditCard",
  },
  core_reporting: {
    key: "core_reporting",
    label: "Reports",
    description: "Standard membership, financial, and event reporting",
    navGroup: "intelligence",
    eligibleTenants: ["org", "association"],
    defaultVisibility: "always",
    isCore: true,
    route: "/reports",
    iconKey: "FileText",
  },

  // ── Org Modules (entitled) ────────────────────────────────────────────────
  sponsorships: {
    key: "sponsorships",
    label: "Sponsorships",
    description: "Sponsor packages, fulfillment, booking, and ROI tracking",
    navGroup: "revenue",
    eligibleTenants: ["org"],
    defaultVisibility: "entitled",
    isCore: false,
    route: "/sponsorship",
    iconKey: "Handshake",
  },
  automations: {
    key: "automations",
    label: "Automations",
    description: "Workflow automation, email journeys, and triggered actions",
    navGroup: "operations",
    eligibleTenants: ["org"],
    defaultVisibility: "entitled",
    isCore: false,
    iconKey: "Route",
  },
  ai_insights: {
    key: "ai_insights",
    label: "AI Insights",
    description: "AI-powered member scoring, recommendations, and predictions",
    navGroup: "intelligence",
    eligibleTenants: ["org", "association"],
    defaultVisibility: "entitled",
    isCore: false,
    route: "/intelligence",
    iconKey: "Brain",
  },
  advanced_reporting: {
    key: "advanced_reporting",
    label: "Advanced Reporting",
    description: "Board reports, executive dashboards, and growth gap analysis",
    navGroup: "intelligence",
    eligibleTenants: ["org", "association"],
    defaultVisibility: "entitled",
    isCore: false,
    iconKey: "BarChart3",
  },
  opportunities: {
    key: "opportunities",
    label: "Opportunities",
    description: "Job board, deals, promotions, and member marketplace",
    navGroup: "engagement",
    eligibleTenants: ["org", "association"],
    defaultVisibility: "entitled",
    isCore: false,
    route: "/opportunities",
    iconKey: "Compass",
  },
  integrations: {
    key: "integrations",
    label: "Integrations",
    description: "Third-party connections, API access, and data sync",
    navGroup: "settings",
    eligibleTenants: ["org", "association"],
    defaultVisibility: "entitled",
    isCore: false,
    iconKey: "Network",
  },
  video_library: {
    key: "video_library",
    label: "Video Library",
    description: "Hosted video for event replays, courses, and member education",
    navGroup: "engagement",
    eligibleTenants: ["org", "association"],
    defaultVisibility: "entitled",
    isCore: false,
    route: "/video-library",
    iconKey: "Video",
  },
  learning_hub: {
    key: "learning_hub",
    label: "Learning Hub",
    description: "Unified member learning portal with courses, replays, and educational content",
    navGroup: "engagement",
    eligibleTenants: ["org", "association"],
    defaultVisibility: "entitled",
    isCore: false,
    route: "/learning-hub",
    iconKey: "GraduationCap",
  },

  assoc_hierarchy: {
    key: "assoc_hierarchy",
    label: "Hierarchy Management",
    description: "Multi-org hierarchy, child org management, and governance",
    navGroup: "association",
    eligibleTenants: ["association"],
    defaultVisibility: "entitled",
    isCore: false,
    iconKey: "Building2",
  },
  assoc_distribution: {
    key: "assoc_distribution",
    label: "Content Distribution",
    description: "Controlled content sharing and downward distribution to child orgs",
    navGroup: "association",
    eligibleTenants: ["association"],
    defaultVisibility: "entitled",
    isCore: false,
    route: "/exchange/distribution",
    iconKey: "Network",
  },
  assoc_exchange: {
    key: "assoc_exchange",
    label: "Network Exchange",
    description: "Bidirectional content/opportunity flow between associations and orgs",
    navGroup: "association",
    eligibleTenants: ["association", "org"],
    defaultVisibility: "entitled",
    isCore: false,
    route: "/exchange",
    iconKey: "RefreshCw",
  },
  assoc_billing: {
    key: "assoc_billing",
    label: "Association Billing",
    description: "Association-level subscription management and child org coverage",
    navGroup: "association",
    eligibleTenants: ["association"],
    defaultVisibility: "entitled",
    isCore: false,
    iconKey: "CreditCard",
  },
  assoc_analytics: {
    key: "assoc_analytics",
    label: "Association Analytics",
    description: "Cross-org metrics, network activity, and association performance",
    navGroup: "association",
    eligibleTenants: ["association"],
    defaultVisibility: "entitled",
    isCore: false,
    route: "/exchange/analytics",
    iconKey: "BarChart3",
  },

  // ── Platform Modules ──────────────────────────────────────────────────────
  platform_entitlements: {
    key: "platform_entitlements",
    label: "Entitlement Management",
    description: "Platform-level module activation and commercial entitlement control",
    navGroup: "platform",
    eligibleTenants: ["platform"],
    defaultVisibility: "entitled",
    isCore: false,
    iconKey: "ShieldCheck",
  },
  platform_billing_ops: {
    key: "platform_billing_ops",
    label: "Billing Operations",
    description: "Platform billing oversight, QA test matrix, and reconciliation",
    navGroup: "platform",
    eligibleTenants: ["platform"],
    defaultVisibility: "entitled",
    isCore: false,
    iconKey: "Receipt",
  },
  platform_support_ops: {
    key: "platform_support_ops",
    label: "Support Operations",
    description: "Platform-level support, impersonation, and internal tooling",
    navGroup: "platform",
    eligibleTenants: ["platform"],
    defaultVisibility: "entitled",
    isCore: false,
    iconKey: "Headset",
  },
  platform_partner_ops: {
    key: "platform_partner_ops",
    label: "Partner Operations",
    description: "Partner management, association onboarding, and network governance",
    navGroup: "platform",
    eligibleTenants: ["platform"],
    defaultVisibility: "entitled",
    isCore: false,
    iconKey: "Users2",
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** All module keys */
export const ALL_MODULE_KEYS = Object.keys(MODULE_REGISTRY);

/** Get module definition (returns undefined if not found) */
export function getModuleDefinition(key: string): ModuleDefinition | undefined {
  return MODULE_REGISTRY[key];
}

/** Filter modules by tenant type */
export function getModulesForTenant(tenantType: TenantType): ModuleDefinition[] {
  return Object.values(MODULE_REGISTRY).filter((m) => m.eligibleTenants.includes(tenantType));
}

/** Filter modules by nav group */
export function getModulesByNavGroup(group: NavGroup): ModuleDefinition[] {
  return Object.values(MODULE_REGISTRY).filter((m) => m.navGroup === group);
}

/** Get all core modules */
export function getCoreModules(): ModuleDefinition[] {
  return Object.values(MODULE_REGISTRY).filter((m) => m.isCore);
}

/** Get all non-core modules */
export function getEntitledModules(): ModuleDefinition[] {
  return Object.values(MODULE_REGISTRY).filter((m) => !m.isCore);
}

/** Nav group labels for rendering */
export const NAV_GROUP_LABELS: Record<NavGroup, string> = {
  core: "Core",
  member_success: "Member Success",
  engagement: "Engagement",
  growth: "Growth",
  revenue: "Revenue",
  operations: "Operations",
  intelligence: "Intelligence",
  association: "Association",
  platform: "Platform",
  settings: "Settings",
};

/** Nav group sort order */
export const NAV_GROUP_ORDER: Record<NavGroup, number> = {
  core: 0,
  member_success: 1,
  engagement: 2,
  growth: 3,
  revenue: 4,
  operations: 5,
  intelligence: 6,
  association: 7,
  platform: 8,
  settings: 9,
};
